import { I18nService } from "@/core/services/i18nService";
import { PortalContextService } from "@/core/services/portal-context.service";
import { StammdatenService } from "@/core/services/stammdaten.service";
import { inject, Inject, Injectable } from "@angular/core";
import { AbstractControl, ValidationErrors, Validator } from "@angular/forms";
import { environment } from "src/environments/environment";

export class PasswordValidator implements Validator {

    constructor(protected translateService: I18nService, protected strongPasswordPolicy: boolean = false, protected forbiddenFormControls: string[] = []) {
    }

    validate(control: AbstractControl): ValidationErrors | null {
        const value = control.value
        if (!value) {
            return { required: this.translateService.instant("register.password_required") };
        }

        if (this.strongPasswordPolicy) {
            return this.validateWithStrongPasswordPolicy(control);
        }
        return this.validateWithWeakPasswordPolicy(value);
    }

    validateWithWeakPasswordPolicy(value: any): ValidationErrors | null {
        const errors: any = {};

        if (value.length < 8) {
            errors.minLength = this.translateService.instant("register.passwordformat_lenght_required");
        }

        const hasUpperCase = /[A-Z]+/.test(value);
        const hasLowerCase = /[a-z]+/.test(value);
        const hasNumeric = /[0-9]+/.test(value);
        const hasSpecial = /[!-\/:-@[-`{-~]/.test(value);

        const isValid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecial;

        if (!isValid) {
            errors.notStrongEnough = this.translateService.instant("register.passwordformat_strenght_required");
        }

        return Object.keys(errors).length ? errors : null;
    }

    validateWithStrongPasswordPolicy(control: AbstractControl): ValidationErrors | null {
        const value = control.value
        const forbiddenWordsRaw = ['xFound'];

        environment.xFoundRestUrl.replace("https://", "").replace("http://", "").split(".").slice(0, -1).forEach(word => {
            forbiddenWordsRaw.push(word);
        });

        environment.xFoundPortalConfig.portalName.split(" ").forEach(word => {
            forbiddenWordsRaw.push(word);
        });

        const parent = control.parent;
        if (parent) {
            this.forbiddenFormControls.forEach((formControlName: string) => {
                const control = parent.get(formControlName);
                const value = control?.value;

                if (value && value.includes("@")) {
                    value.split("@")[0].split(".").forEach((word: string) => {
                        const firstChar = word.charAt(0).toUpperCase();
                        const rest = word.slice(1).toLowerCase();
                        forbiddenWordsRaw.push(firstChar + rest);
                    });
                }
                else {
                    forbiddenWordsRaw.push(value);
                }
            });
        }

        //make unique
        const seen = new Set<string>();
        const forbiddenWords: string[] = [];
        for (const word of forbiddenWordsRaw) {
            const lower = word.toLowerCase();
            if (!seen.has(lower)) {
                seen.add(lower);
                forbiddenWords.push(word);
            }
        }

        const errors: any = {};

        if (value.length < 12) {
            errors.minLength = this.translateService.instant("register.strongpolicy.passwordformat_lenght_required");
        }

        const criteria = {
            hasUpperCase: /[A-Z]/.test(value),
            hasLowerCase: /[a-z]/.test(value),
            hasNumber: /[0-9]/.test(value),
            hasSpecialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(value)
        };

        const metCriteria = Object.values(criteria).filter(v => v).length;
        if (metCriteria < 3) {
            errors.characterVariety = this.translateService.instant("register.strongpolicy.passwordformat_strenght_required");
        }

        if (forbiddenWords.some(word => value.toLowerCase().includes(word.toLowerCase()))) {
            errors.forbiddenWords = this.translateService.instant("register.strongpolicy.password_contains_blacklisted_words") + forbiddenWords.join(", ");
        }

        if (this.hasSequentialChars(value)) {
            errors.sequential = this.translateService.instant("register.strongpolicy.password_contains_sequentiel_characters");
        }

        return Object.keys(errors).length ? errors : null;
    }

    hasSequentialChars(value: string, sequenceLength: number = 4): boolean {
        const normalized = value.toLowerCase();

        // Tastaturmuster, die häufig verwendet werden
        const keyboardPatterns = [
            'asdfghjkl',
            //deutsche Tastatur
            'qwertzuiop',
            'yxcvbnm',
            //englische Tastatur
            'qwertyuiop',
            'zxcvbnm',
        ];

        for (let i = 0; i <= normalized.length - sequenceLength; i++) {
            const slice = normalized.slice(i, i + sequenceLength);

            // Zeichenweise aufsteigend/absteigend prüfen
            let isSequentialAsc = true;
            let isSequentialDesc = true;
            for (let j = 0; j < slice.length - 1; j++) {
                const curr = slice.charCodeAt(j);
                const next = slice.charCodeAt(j + 1);
                if (curr + 1 !== next) isSequentialAsc = false;
                if (curr - 1 !== next) isSequentialDesc = false;
            }
            if (isSequentialAsc || isSequentialDesc) return true;

            // In Tastaturmuster enthalten?
            for (const pattern of keyboardPatterns) {
                if (pattern.includes(slice)) return true;
                if (pattern.split('').reverse().join('').includes(slice)) return true;
            }
        }

        return false;
    }
}